"use client";

/**
 * Custom DatePicker component using flatpickr.
 * 
 * Requirements Summary:
 * - Provides a polished, standardized date selection UI across the application.
 * - Supports single, multiple, range, and time modes.
 * - Enhanced with error handling, hint text, and required indicators.
 * - Supports timezone-safe date handling when used with dateUtils.
 * - Modal-friendly with appendTo support to prevent z-index issues.
 */

import { useEffect } from "react";
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.css";
import Label from "./Label";
import GoogleMaterialIcon from "@/components/ui/GoogleMaterialIcon";
import Hook = flatpickr.Options.Hook;
import DateOption = flatpickr.Options.DateOption;

type PropsType = {
  id: string;
  mode?: "single" | "multiple" | "range" | "time";
  onChange?: Hook | Hook[];
  defaultDate?: DateOption | DateOption[];
  label?: string;
  placeholder?: string;
  appendTo?: HTMLElement; // ⭐ NEW: Allow calendar to append to body for modals (added 2025-10-22)
  required?: boolean; // ⭐ NEW: Show required indicator on label
  minDate?: DateOption; // ⭐ NEW: Allow setting a minimum date
  maxDate?: DateOption; // ⭐ NEW: Allow setting a maximum date
  error?: boolean | string; // ⭐ NEW: Error state and message
  hint?: string | React.ReactNode; // ⭐ NEW: Help text or hint
  position?: "auto" | "above" | "below" | "left" | "right" | "auto left" | "auto right"; // ⭐ NEW: Flatpickr position (added 2026-01-28)
};

export default function DatePicker({
  id,
  mode,
  onChange,
  label,
  defaultDate,
  placeholder,
  appendTo,
  required,
  minDate,
  maxDate,
  error,
  hint,
  position = "auto",
}: PropsType) {
  useEffect(() => {
    // Time-only mode needs flatpickr's `enableTime + noCalendar` plus a
    // matching `dateFormat` — without those it falls back to a calendar
    // picker that displays dates instead of "9:00 AM" style strings.
    // Other modes keep the previous date-only behavior.
    const isTimeMode = mode === "time";
    const flatPickr = flatpickr(`#${id}`, {
      mode: isTimeMode ? "single" : (mode || "single"),
      enableTime: isTimeMode,
      noCalendar: isTimeMode,
      time_24hr: false,
      static: !appendTo, // ⭐ When appendTo is provided, disable static positioning
      appendTo: appendTo, // ⭐ Append calendar to specified element (e.g., document.body for modals)
      disableMobile: true, // ⭐ Disable mobile mode and arrow pointer
      monthSelectorType: "static",
      dateFormat: isTimeMode ? "h:i K" : mode === "range" ? "M d, Y" : "D M d, Y",
      position, // ⭐ Pass position option to flatpickr
      locale: {
        rangeSeparator: " - ",
      },
      defaultDate,
      minDate,
      maxDate,
      onChange,
    });

    return () => {
      if (!Array.isArray(flatPickr)) {
        flatPickr.destroy();
      }
    };
  }, [mode, onChange, id, defaultDate, appendTo]);

  return (
    <div className="w-full">
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="ml-1 text-error-500">*</span>}
        </Label>
      )}

      <div className="relative w-full">
        <input
          id={id}
          placeholder={placeholder}
          className={`h-11 w-full rounded-lg border appearance-none pl-4 pr-12 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-hidden focus:ring-3 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 bg-transparent text-gray-800 text-center ${
            error 
              ? 'border-error-500 focus:border-error-300 focus:ring-error-500/20 dark:border-error-500' 
              : 'border-gray-300 focus:border-brand-300 focus:ring-brand-500/20 dark:border-gray-700'
          } dark:focus:border-brand-800`}
        />

        <span className={`absolute -translate-y-1/2 pointer-events-none right-3 top-1/2 ${error ? 'text-error-500' : 'text-gray-500 dark:text-gray-400'}`}>
          <GoogleMaterialIcon name={mode === "time" ? "schedule" : "calendar_month"} size="md" />
        </span>
      </div>
      {(hint || (typeof error === 'string' && error)) && (
        <p className={`mt-1.5 text-xs ${error ? 'text-error-500' : 'text-gray-500 dark:text-gray-400'}`}>
          {typeof error === 'string' ? error : hint}
        </p>
      )}
    </div>
  );
}

